import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { useToast } from '@/shared/components/Toast';
import {
  vectorDbKeys,
  useVectorDbSyncCheck,
  useSyncProvidersVectorDb,
  useSyncResourcesVectorDb,
} from '../hooks/useVectorDb';
import { JobHistoryTable } from './JobHistoryTable';
import type { VectorDbSyncName, VectorDbSyncCheckResult } from '../types/vector-db.type';

const STORE_LABELS: Record<VectorDbSyncName, string> = {
  providers: 'Providers',
  resources: 'Resources',
};

// Trimmed from the Care Engine's full message — its "Poll GET /background-jobs/..."
// tail is an internal implementation detail we deliberately don't act on (see the
// revert to message-only display), so it shouldn't surface to the admin.
const SYNC_QUEUED_MESSAGE = 'Full sync accepted and running in the background.';

const TH_BASE = 'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';
const TD_BASE = 'px-4 py-4 whitespace-nowrap text-sm text-gray-900';

type VectorDbRowProps = {
  name: VectorDbSyncName;
  check: ReturnType<typeof useVectorDbSyncCheck>;
  onSync: () => void;
  isSyncing: boolean;
};

function VectorDbRow({ name, check, onSync, isSyncing }: VectorDbRowProps) {
  const data: VectorDbSyncCheckResult | undefined = check.data;

  return (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className={`${TD_BASE} font-medium`}>{STORE_LABELS[name]}</td>
      <td className={TD_BASE}>
        {check.isLoading ? (
          <span className="flex items-center gap-1.5 text-gray-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking…
          </span>
        ) : check.isError ? (
          <span className="text-red-600 text-xs">Failed to check</span>
        ) : data ? (
          <Badge
            variant={data.in_sync ? 'secondary' : 'destructive'}
            className={data.in_sync ? 'bg-green-100 text-green-800 border-green-200' : ''}
          >
            {data.in_sync ? (
              <>
                <CheckCircle2 className="h-3 w-3" /> In sync
              </>
            ) : (
              <>
                <XCircle className="h-3 w-3" /> Out of sync
              </>
            )}
          </Badge>
        ) : null}
      </td>
      <td className={TD_BASE}>{data?.postgres_count ?? '—'}</td>
      <td className={TD_BASE}>{data?.vector_store_file_count ?? '—'}</td>
      <td className={TD_BASE}>{data?.difference ?? '—'}</td>
      <td className={TD_BASE}>
        <Button size="sm" variant="outline" onClick={onSync} disabled={isSyncing}>
          {isSyncing ? (
            <>
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Syncing…
            </>
          ) : (
            'Sync Now'
          )}
        </Button>
      </td>
    </tr>
  );
}

export function VectorDbTab() {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const providersCheck = useVectorDbSyncCheck('providers');
  const resourcesCheck = useVectorDbSyncCheck('resources');
  const syncProviders = useSyncProvidersVectorDb();
  const syncResources = useSyncResourcesVectorDb();

  const refreshAll = () => {
    providersCheck.refetch();
    resourcesCheck.refetch();
    queryClient.invalidateQueries({ queryKey: vectorDbKeys.all });
  };

  const triggerProvidersSync = () => {
    syncProviders.mutate(undefined, {
      onSuccess: () => showToast(SYNC_QUEUED_MESSAGE, 'success'),
      onError: (error) => showToast(error.message || 'Failed to start providers sync', 'error'),
    });
  };

  const triggerResourcesSync = () => {
    syncResources.mutate(undefined, {
      onSuccess: () => showToast(SYNC_QUEUED_MESSAGE, 'success'),
      onError: (error) => showToast(error.message || 'Failed to start resources sync', 'error'),
    });
  };

  return (
    <div data-testid="vector-db-tab" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Manage Vector DB</h2>
          <p className="text-gray-600 mt-1">
            Check whether the providers and resources vector stores match Postgres, and resync when they drift.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={refreshAll}
          disabled={providersCheck.isFetching || resourcesCheck.isFetching}
        >
          <RefreshCw
            className={`h-4 w-4 mr-1.5 ${providersCheck.isFetching || resourcesCheck.isFetching ? 'animate-spin' : ''}`}
          />
          Refresh
        </Button>
      </div>

      <div className="flex flex-col border border-gray-200 rounded-md bg-white">
        <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200 text-sm">
          <span>
            Total <span className="font-semibold text-gray-900">2</span> vector stores
          </span>
        </div>

        <div className="overflow-auto">
          <table className="w-full" role="table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className={TH_BASE}>Store</th>
                <th className={TH_BASE}>Status</th>
                <th className={TH_BASE}>Postgres Count</th>
                <th className={TH_BASE}>Vector Store File Count</th>
                <th className={TH_BASE}>Difference</th>
                <th className={TH_BASE}>Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <VectorDbRow
                name="providers"
                check={providersCheck}
                onSync={triggerProvidersSync}
                isSyncing={syncProviders.isPending}
              />
              <VectorDbRow
                name="resources"
                check={resourcesCheck}
                onSync={triggerResourcesSync}
                isSyncing={syncResources.isPending}
              />
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-600">
          <span>Displaying 2 rows</span>
        </div>
      </div>

      <JobHistoryTable name="providers" />
      <JobHistoryTable name="resources" />
    </div>
  );
}
